import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Accessible title; rendered visibly via DialogTitle if used inside. */
  labelledBy?: string;
}

/**
 * Minimal modal dialog. No external dependency — focus stays simple, the
 * overlay is click-to-dismiss, Escape closes.
 */
function Dialog({ open, onClose, children, labelledBy }: DialogProps): JSX.Element | null {
  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/30 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className="w-full max-w-lg rounded-lg border border-border bg-card"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function DialogHeader({
  title,
  description,
  onClose,
  id
}: {
  title: string;
  description?: string;
  onClose: () => void;
  id?: string;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between border-b border-border p-5">
      <div className="space-y-1">
        <h2 id={id} className="text-base font-semibold tracking-tight">
          {title}
        </h2>
        {description ? (
          <p className="text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="rounded-md p-1 text-muted-foreground hover:bg-secondary"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function DialogBody({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return <div className={cn("space-y-4 p-5", className)} {...props} />;
}

function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div
      className={cn(
        "flex justify-end gap-2 border-t border-border p-5",
        className
      )}
      {...props}
    />
  );
}

export { Dialog, DialogHeader, DialogBody, DialogFooter };
